import React, { useState, useEffect, useCallback } from "react";
import hosys from "@/api/api";
import { Button } from "@/components/ui/button";
import moment from "moment";
import "moment/locale/ca";
import {
  ChevronLeft, ChevronRight, Save, Loader2, AlertTriangle,
  RefreshCw, Moon,
} from "lucide-react";

import { GridHeader, GridDayCell, WeeklySummaryCell, MonthlySummaryCell } from "./GridParts";

moment.locale("ca");

// ── helpers ────────────────────────────────────────────────────────────────

// Build segments from raw TimeRecord rows for a given day
// Use local time (moment local) to display hours correctly
function buildSegmentsFromRecords(records) {
  const sorted = [...records]
    .filter(r => !r.is_deleted)
    .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  const segments = [];
  let pendingEntry = null;
  for (const r of sorted) {
    if (r.type === "entry") {
      pendingEntry = moment(r.timestamp).local().format("HH:mm");
    } else if (r.type === "exit" && pendingEntry !== null) {
      segments.push({ entry: pendingEntry, exit: moment(r.timestamp).local().format("HH:mm") });
      pendingEntry = null;
    }
  }
  // open entry with no exit
  if (pendingEntry !== null) segments.push({ entry: pendingEntry, exit: "" });

  return segments.length > 0 ? segments : [{ entry: "", exit: "" }];
}

/**
 * Calculates total worked minutes from an array of segments.
 * If exit < entry (night shift crossing midnight), exit is treated as next day.
 */
function calcSegmentsMins(segments, date) {
  let total = 0;
  for (const seg of segments) {
    if (!seg.entry || !seg.exit) continue;
    const entry = moment(`${date} ${seg.entry}`, "YYYY-MM-DD HH:mm");
    let   exit  = moment(`${date} ${seg.exit}`,  "YYYY-MM-DD HH:mm");
    if (!entry.isValid() || !exit.isValid()) continue;
    // Night shift: exit is on next day
    if (exit.isBefore(entry)) exit.add(1, "day");
    const mins = exit.diff(entry, "minutes");
    if (mins > 0 && mins <= 1440) total += mins;
  }
  return total;
}

function minutesToHHMM(mins) {
  if (!mins) return "";
  return `${Math.floor(mins / 60)}h${(mins % 60).toString().padStart(2, "0")}`;
}

// Is exit time crossing midnight relative to entry?
function isNightShift(entryStr, exitStr) {
  if (!entryStr || !exitStr) return false;
  const e = moment(`2000-01-01 ${entryStr}`, "YYYY-MM-DD HH:mm");
  const s = moment(`2000-01-01 ${exitStr}`,  "YYYY-MM-DD HH:mm");
  return s.isValid() && e.isValid() && s.isBefore(e);
}

const DAYS_CA = ["Dl", "Dm", "Dc", "Dj", "Dv", "Ds", "Dg"];
const WEEKEND = [6, 0];

// Group days into ISO weeks: returns [[day, day, ...], [day, day, ...], ...]
function groupByWeek(days) {
  const weeks = [];
  let currentWeek = [];
  let currentIsoWeek = null;
  for (const d of days) {
    const iw = d.isoWeek();
    if (currentIsoWeek !== null && iw !== currentIsoWeek) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
    currentWeek.push(d);
    currentIsoWeek = iw;
  }
  if (currentWeek.length > 0) weeks.push(currentWeek);
  return weeks;
}

// ── component ─────────────────────────────────────────────────────────────
export default function MonthlyGridEditor({ venueId, members }) {
  const [yearMonth, setYearMonth] = useState(moment().format("YYYY-MM"));
  const [records, setRecords]     = useState([]);
  // overrides: { "email|date": [ { entry: "HH:mm", exit: "HH:mm" }, ... ] }
  const [overrides, setOverrides] = useState({});
  const [dirty, setDirty]         = useState({});
  const [loading, setLoading]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [saveCount, setSaveCount] = useState(0);
  const [contracts, setContracts] = useState([]);

  const start = moment(yearMonth, "YYYY-MM").startOf("month");
  const end   = moment(yearMonth, "YYYY-MM").endOf("month");
  const days  = [];
  for (let d = start.clone(); d.isSameOrBefore(end); d.add(1, "day")) days.push(d.clone());

  const activeMembers = members.filter(m => m.active !== false);

  const load = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    const dateFrom = start.format("YYYY-MM-DD");
    const dateTo   = end.format("YYYY-MM-DD");

    const [recs, cts] = await Promise.all([
      hosys.entities.TimeRecord.filter({
        venue_id: venueId,
        date_from: dateFrom,
        date_to: dateTo,
        limit: 2000,
      }, "date"),
      hosys.entities.WorkerContract.filter({ venue_id: venueId, active: true }),
    ]);

    setRecords(recs);
    setContracts(cts);
    setOverrides({});
    setDirty({});
    setLoading(false);
  }, [venueId, yearMonth]);

  useEffect(() => { load(); }, [load]);

  // Get segments for a cell (override wins over records)
  const getCellSegments = (email, date) => {
    const key = `${email}|${date}`;
    if (overrides[key]) return overrides[key];
    const dayRecs = records.filter(r => r.user_email === email && r.date === date);
    return buildSegmentsFromRecords(dayRecs);
  };

  const updateSegment = (email, date, idx, field, val) => {
    const key = `${email}|${date}`;
    const segs = getCellSegments(email, date).map((s, i) =>
      i === idx ? { ...s, [field]: val } : s
    );
    setOverrides(o => ({ ...o, [key]: segs }));
    setDirty(d => ({ ...d, [key]: true }));
  };

  const addSegment = (email, date) => {
    const key = `${email}|${date}`;
    const segs = [...getCellSegments(email, date), { entry: "", exit: "" }];
    setOverrides(o => ({ ...o, [key]: segs }));
    setDirty(d => ({ ...d, [key]: true }));
  };

  const removeSegment = (email, date, idx) => {
    const key = `${email}|${date}`;
    const segs = getCellSegments(email, date).filter((_, i) => i !== idx);
    const final = segs.length === 0 ? [{ entry: "", exit: "" }] : segs;
    setOverrides(o => ({ ...o, [key]: final }));
    setDirty(d => ({ ...d, [key]: true }));
  };

  const getRowTotalMins = (email) => {
    let total = 0;
    days.forEach(d => {
      const date = d.format("YYYY-MM-DD");
      total += calcSegmentsMins(getCellSegments(email, date), date);
    });
    return total;
  };

  const getColTotalMins = (date) => {
    let total = 0;
    activeMembers.forEach(m => {
      total += calcSegmentsMins(getCellSegments(m.user_email, date), date);
    });
    return total;
  };

  const dirtyCount = Object.keys(dirty).length;

  const handleSave = async () => {
    setSaving(true);
    const toSave = Object.keys(dirty);

    // Build payload for backend function — one single API call
    const days = toSave.map(key => {
      const [email, date] = key.split("|");
      const segments = overrides[key] || [];
      const member   = activeMembers.find(m => m.user_email === email);
      const userName = member?.user_name || email;

      const segsWithTs = segments.map(seg => {
        if (!seg.entry) return null;
        // parseZone=false → moment interprets as LOCAL time and converts to UTC correctly
        const entryTs = moment(`${date}T${seg.entry}:00`).toISOString();
        let exitTs = null;
        if (seg.exit) {
          let exitMom = moment(`${date}T${seg.exit}:00`);
          if (isNightShift(seg.entry, seg.exit)) exitMom.add(1, "day");
          exitTs = exitMom.toISOString();
        }
        return { entry: seg.entry, exit: seg.exit, entryTs, exitTs };
      }).filter(Boolean);

      return { email, userName, date, segments: segsWithTs };
    });

    const res = await hosys.functions.invoke("saveTimeRecords", { venueId, days });
    setSaveCount(res.data?.saved || days.length);
    await load();
    setSaving(false);
  };

  const prevMonth = () => setYearMonth(moment(yearMonth, "YYYY-MM").subtract(1, "month").format("YYYY-MM"));
  const nextMonth = () => setYearMonth(moment(yearMonth, "YYYY-MM").add(1, "month").format("YYYY-MM"));
  const grandTotal = activeMembers.reduce((acc, m) => acc + getRowTotalMins(m.user_email), 0);

  // Week grouping
  const weeks = groupByWeek(days);

  // Weekly total per worker
  const getWeekMins = (email, weekDays) =>
    weekDays.reduce((sum, d) => sum + calcSegmentsMins(getCellSegments(email, d.format("YYYY-MM-DD")), d.format("YYYY-MM-DD")), 0);

  // Weekly total all workers
  const getWeekColTotal = (weekDays) =>
    activeMembers.reduce((sum, m) => sum + getWeekMins(m.user_email, weekDays), 0);

  // Contract helpers
  const getContract = (email) => contracts.find(c => c.user_email === email);
  const getContractWeeklyMins = (email) => {
    const c = getContract(email);
    return c?.weekly_hours ? Math.round(c.weekly_hours * 60) : 0;
  };
  const getContractMonthlyMins = (email) => {
    // Monthly = weekly * (working weeks in this month)
    const weeklyMins = getContractWeeklyMins(email);
    if (!weeklyMins) return 0;
    const numWeeks = weeks.length;
    return Math.round(weeklyMins * (numWeeks >= 5 ? 4.33 : numWeeks));
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Regularització mensual d'hores</h2>
          <p className="text-xs text-gray-500 mt-0.5">Introdueix entrades, sortides i pauses de cada dia per treballador.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={prevMonth}><ChevronLeft className="w-4 h-4" /></Button>
          <span className="text-sm font-bold text-gray-800 min-w-[120px] text-center capitalize">
            {moment(yearMonth, "YYYY-MM").format("MMMM YYYY")}
          </span>
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={nextMonth}><ChevronRight className="w-4 h-4" /></Button>
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={load} title="Recarregar"><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </div>

      {/* Info */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-700">
          <span className="font-semibold text-amber-800">Regularització manual — </span>
          Pots afegir múltiples trams (E/S) per dia. Si la sortida és anterior a l'entrada <Moon className="inline w-3 h-3 text-indigo-500" /> es comptarà com a torn nocturn (sortida del dia següent).
        </p>
      </div>

      {/* Save bar */}
      {dirtyCount > 0 && (
        <div className="flex items-center justify-between bg-[#7B2D3F]/5 border border-[#7B2D3F]/20 rounded-xl px-4 py-3">
          <span className="text-sm text-[#7B2D3F] font-semibold">{dirtyCount} dia{dirtyCount !== 1 ? "es" : ""} modificat{dirtyCount !== 1 ? "s" : ""}</span>
          <Button onClick={handleSave} disabled={saving} className="bg-[#7B2D3F] hover:bg-[#5B1D2F] gap-2 h-8">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Desar regularització
          </Button>
        </div>
      )}

      {saveCount > 0 && dirtyCount === 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-sm text-emerald-700 font-medium">
          ✓ {saveCount} dia{saveCount !== 1 ? "es" : ""} actualitzat{saveCount !== 1 ? "s" : ""} correctament.
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Carregant registres...
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
          <table className="w-full text-xs border-collapse">
            <GridHeader weeks={weeks} />
            <tbody>
              {activeMembers.map((m, mi) => {
                const rowMins = getRowTotalMins(m.user_email);
                const rowBg   = mi % 2 === 0 ? "#fff" : "#fafafa";
                return (
                  <tr key={m.user_email}>
                    <td className="sticky left-0 z-10 px-3 py-2 border-r border-gray-200 border-b border-gray-100 font-medium text-gray-800 align-top"
                        style={{ background: rowBg }}>
                      <div className="truncate max-w-[145px]" title={m.user_name || m.user_email}>
                        {m.user_name || m.user_email}
                      </div>
                      {getContract(m.user_email) && (
                        <div className="text-[10px] text-blue-500 font-semibold">
                          Contracte: {getContract(m.user_email).weekly_hours}h/set
                        </div>
                      )}
                      {m.dni_nie && <div className="text-[10px] text-gray-400">{m.dni_nie}</div>}
                    </td>

                    {weeks.map((weekDays, wi) => (
                      <React.Fragment key={`wd-${wi}`}>
                        {weekDays.map(d => {
                          const date     = d.format("YYYY-MM-DD");
                          const cellKey  = `${m.user_email}|${date}`;
                          const isDirty  = !!dirty[cellKey];
                          const segs     = getCellSegments(m.user_email, date);
                          const isWeekend = WEEKEND.includes(d.day());
                          const cellMins  = calcSegmentsMins(segs, date);

                          return (
                            <GridDayCell
                              key={date}
                              date={date}
                              segs={segs}
                              isDirty={isDirty}
                              isWeekend={isWeekend}
                              cellMins={cellMins}
                              isNightShift={isNightShift}
                              minutesToHHMM={minutesToHHMM}
                              onUpdateSegment={updateSegment}
                              onAddSegment={addSegment}
                              onRemoveSegment={removeSegment}
                              email={m.user_email}
                            />
                          );
                        })}
                        <WeeklySummaryCell
                          actual={getWeekMins(m.user_email, weekDays)}
                          contracted={getContractWeeklyMins(m.user_email)}
                          minutesToHHMM={minutesToHHMM}
                        />
                      </React.Fragment>
                    ))}

                    <MonthlySummaryCell
                      actual={rowMins}
                      contracted={getContractMonthlyMins(m.user_email)}
                      minutesToHHMM={minutesToHHMM}
                    />
                  </tr>
                );
              })}

              {/* Bottom totals row */}
              <tr className="bg-gray-100 border-t-2 border-gray-300 font-bold">
                <td className="sticky left-0 z-10 bg-gray-100 px-3 py-2 text-gray-600 border-r border-gray-200 text-[10px] uppercase tracking-wide">
                  Total dia
                </td>
                {weeks.map((weekDays, wi) => (
                  <React.Fragment key={`tf-${wi}`}>
                    {weekDays.map(d => {
                      const total = getColTotalMins(d.format("YYYY-MM-DD"));
                      return (
                        <td key={d.format("YYYY-MM-DD")} className="text-center py-2 border-r border-gray-200 text-gray-700 text-xs">
                          {total > 0 ? minutesToHHMM(total) : ""}
                        </td>
                      );
                    })}
                    {/* Weekly total all workers */}
                    <td className="text-center px-1 py-2 font-bold text-indigo-800 bg-indigo-100 border-r border-indigo-200 text-xs">
                      {getWeekColTotal(weekDays) > 0 ? minutesToHHMM(getWeekColTotal(weekDays)) : ""}
                    </td>
                  </React.Fragment>
                ))}
                {/* Grand total */}
                <td className="text-center px-2 py-2 text-[#7B2D3F] font-bold text-sm bg-[#7B2D3F]/10">
                  {minutesToHHMM(grandTotal)}
                </td>
              </tr>

              {/* Contract hours row */}
              <tr className="bg-blue-50 border-t border-blue-200">
                <td className="sticky left-0 z-10 bg-blue-50 px-3 py-1.5 text-blue-600 border-r border-blue-200 text-[10px] uppercase tracking-wide font-semibold">
                  Contracte
                </td>
                {weeks.map((weekDays, wi) => (
                  <React.Fragment key={`ct-${wi}`}>
                    {weekDays.map(d => (
                      <td key={d.format("YYYY-MM-DD")} className="border-r border-blue-100" />
                    ))}
                    <td className="text-center px-1 py-1.5 text-blue-600 font-semibold bg-blue-100 border-r border-blue-200 text-[10px]">
                      {(() => {
                        const total = activeMembers.reduce((s, m) => s + getContractWeeklyMins(m.user_email), 0);
                        return total > 0 ? minutesToHHMM(total) : "";
                      })()}
                    </td>
                  </React.Fragment>
                ))}
                <td className="text-center px-2 py-1.5 text-blue-700 font-bold text-xs bg-blue-100">
                  {(() => {
                    const total = activeMembers.reduce((s, m) => s + getContractMonthlyMins(m.user_email), 0);
                    return total > 0 ? minutesToHHMM(total) : "";
                  })()}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <p className="text-[10px] text-gray-400 text-center">
        <span className="text-green-600 font-bold">E</span> = Entrada &nbsp;·&nbsp;
        <span className="text-red-500 font-bold">S</span> = Sortida &nbsp;·&nbsp;
        <Moon className="inline w-3 h-3 text-indigo-400" /> = Torn nocturn (sortida dia següent) &nbsp;·&nbsp;
        Cel·les en groc = canvis pendents
      </p>
    </div>
  );
}